import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

export const FIELD_SELECTOR = 'input, textarea, [contenteditable="true"]';
const BOTTOM_GAP = 24;

// The one reveal rule, shared with useKeyboardAvoidance (which works out visibleBottom from
// visualViewport on the web): a field that does not end BOTTOM_GAP above the visible bottom
// is centred.
export const revealField = (field: HTMLElement, visibleBottom: number) => {
  if (field.getBoundingClientRect().bottom + BOTTOM_GAP <= visibleBottom) return;
  field.scrollIntoView({ block: 'center', behavior: 'smooth' });
};

// The plugin also unhooks the web view's own keyboard handling, so WebKit no longer
// scrolls the focused field into view (visualViewport never shrinks either). A field the
// user tapped low on the screen is left under the keyboard, or below the bottom of the box
// it scrolls in once the inset has shrunk that box (an onboarding step ends at its
// footer), so the visible bottom is whichever of those two edges is higher on screen.
const revealFocusedField = (keyboardHeight: number) => {
  const field = document.activeElement;
  if (!(field instanceof HTMLElement) || !field.matches(FIELD_SELECTOR)) return;
  let visibleBottom = window.innerHeight - keyboardHeight;
  for (let box = field.parentElement; box; box = box.parentElement) {
    if (/auto|scroll/.test(getComputedStyle(box).overflowY)) {
      visibleBottom = Math.min(visibleBottom, box.getBoundingClientRect().bottom);
    }
  }
  revealField(field, visibleBottom);
};

// Native only: the web view keeps its full height while the keyboard is up, so we
// hand the keyboard's space back ourselves via --keyboard-inset (#root padding-bottom
// in index.css, plus every position: fixed container that has to shrink with it).
// keyboardWillShow fires at the start of the keyboard animation, so the layout is
// final before the keyboard is even visible — the plugin's own resize modes wait for
// the animation to end and feel delayed.
// Android needs this as much as iOS: the `resize` option in capacitor.config.ts is
// read by the iOS plugin only (the Android side reads `resizeOnFullScreen`), and with
// targetSdk 36 the system runs edge-to-edge and never shrinks the web view. The
// plugin does emit keyboardWillShow with the height on both platforms.
// On the web there is no plugin to listen to — useKeyboardAvoidance covers that from
// visualViewport instead, so the variable always has exactly one writer.
export const initKeyboardInset = () => {
  if (!Capacitor.isNativePlatform()) return;

  const set = (px: number) => {
    const root = document.documentElement;
    root.style.setProperty('--keyboard-inset', `${px}px`);
    if (px > 0) root.dataset.keyboard = 'open';
    else delete root.dataset.keyboard;
  };

  // The reveal runs on keyboardWillShow only: iOS sends it again when focus moves to another
  // field with the keyboard up, and keyboardDidShow only repeats the height.
  void Keyboard.addListener('keyboardWillShow', ({ keyboardHeight }) => {
    set(keyboardHeight);
    if (keyboardHeight > 0) revealFocusedField(keyboardHeight);
  });
  void Keyboard.addListener('keyboardDidShow', ({ keyboardHeight }) => set(keyboardHeight));
  void Keyboard.addListener('keyboardWillHide', () => set(0));
  void Keyboard.addListener('keyboardDidHide', () => set(0));
};
